import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_U, DBO_V } from '@op/utils/format';
import { LanguageType } from '@op/types/common';
import { TableColumn } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import VideoInfoCell from '@opm/components/VideoInfoCell';
import MetricProgress from '@opm/components/MetricProgress';
import { BILIBILI_SORT_OPTIONS, BILIBILI_TABLE_METRICS } from '../constants';
import './index.scss';
import { DBOM_CONTENT } from '@opm/utils';

interface IBilibiliVideoTableProps {
  sortBy: any;
  setSortBy: (value: any) => void;
  language: LanguageType;
  tableRecords?: any[];
  loading?: boolean;
  total?: number;
  pageInfo: any;
  changePageInfo: (value: any) => void;
  /** 各指标全局最大值（接口随 list 返回），用作 MetricProgress 进度条的分母 */
  max?: Record<string, number>;
}

const FIRST_COL_WIDTH = 580;
const METRIC_COL_WIDTH = 140;
const EMPTY_CELL = '-';

/**
 * Bilibili 视频表格（按 2026-05-25 需求 6 列版本）
 *
 * - 第一列：复用 <VideoInfoCell> 渲染视频信息
 * - 2~7 列：6 个指标列，全部 B 站 API 返回真实数据，全部支持排序
 *   * 数值 + MetricProgress 进度条（当前值占当前页该列最大值的比例）
 *   * 空值 / -1 渲染为 '-'，进度条按 0 处理
 */
export default function BilibiliVideoTable({
  sortBy,
  setSortBy,
  language,
  tableRecords = [],
  loading,
  total,
  pageInfo,
  changePageInfo,
  max = {},
}: IBilibiliVideoTableProps) {
  const { intl, isEn } = useIntls();

  const columns: TableColumn<any>[] = useMemo(() => {
    const videoColumn: TableColumn<any> = {
      key: 'publish_time',
      header: intl({ id: 'Video (Sort by Date)' }),
      width: FIRST_COL_WIDTH,
      render: (data: any) => {
        return (
          <VideoInfoCell
            data={data}
            fieldMapping={{
              cover: 'video_cover',
              title: (d: any) => DBOM_CONTENT.videoTitle(language, d),
              channelName: () => 'bilibili',
              authorName: 'author_name',
              publishTime: 'publish_time',
              videoUrl: 'video_url',
              duration: 'video_duration'
            }}
            desItems={[
              {
                type: 'text',
                field: (d: any) => DBOM_CONTENT.channelNameGlobal(isEn, d),
                className: 'channel',
              },
              {
                type: 'text',
                field: (d: any) => DBOM_CONTENT.videoTagGlobal(isEn, d),
                className: 'tag',
              },
              // 发布时间
              { type: 'time', field: 'publish_time' },
            ]}
            channelLabelOnClick={() => {
              DBO_U.open(data.author_url);
            }}
          />
        );
      },
    };

    const metricColumns: TableColumn<any>[] = BILIBILI_TABLE_METRICS.map(
      (metric) => ({
        key: metric.key,
        header: intl({ id: metric.label }),
        align: 'right',
        width: METRIC_COL_WIDTH,
        className: 'dbom-bili-video-metric-cell',
        render: (cvm: any) => {
          const formatFn = metric.format || DBO_V.reviews;
          const value = cvm?.[metric.key];
          const isEmpty = value === null || value === undefined || value === -1;
          return (
            <div>
              {isEmpty ? EMPTY_CELL : formatFn(value)}
              <MetricProgress
                biggest={max[metric.key] || 0}
                count={isEmpty ? 0 : value}
              />
            </div>
          );
        },
      }),
    );

    return [videoColumn, ...metricColumns];
  }, [intl, language, max]);

  /** 全部 metric 列均可排序（与 API sort_item 白名单一致） */
  const sortColumns = useMemo(
    () => BILIBILI_SORT_OPTIONS.map((m) => m.value),
    [],
  );

  return (
    <TableCommonHook
      sortInfo={[sortBy, setSortBy]}
      records={tableRecords}
      total={total}
      loading={loading}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={sortColumns}
      recordKey={(item: any) =>
        item?.video_id ||
        `${item?.video_title || ''}_${item?.publish_time || ''}_${item?.author_uid || ''
        }`
      }
      columns={columns}
      minWidth="auto"
      tableSticky
      pageSizeOptions={[10, 20, 30, 40, 50, 100, 200]}
      className="dbom-bili-video-table"
      verticalTop={false}
    />
  );
}
